import { type UserSection } from '@/models/domain/user'

export type SectionGroupKey =
  | 'locations'
  | 'products'
  | 'inventory'
  | 'discounts'
  | 'otu'
  | 'loyalty'
  | 'messages'
  | 'promotions'
  | 'pos_users'
  | 'oos_ledger'
  | 'reports'

export interface SectionOption {
  id: UserSection
  name: string
  description: string
  group: SectionGroupKey
}

/**
 * Display order and labels for portal-section groups in the user-edit form.
 * Order is alphabetical by label.
 */
export const SECTION_GROUPS: { key: SectionGroupKey; label: string }[] = [
  { key: 'discounts', label: 'Discounts' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'locations', label: 'Locations' },
  { key: 'loyalty', label: 'Loyalty' },
  { key: 'messages', label: 'Messages' },
  { key: 'oos_ledger', label: 'OOS Ledger' },
  { key: 'otu', label: 'OTU Codes' },
  { key: 'pos_users', label: 'POS Users' },
  { key: 'products', label: 'Products' },
  { key: 'promotions', label: 'Promotions' },
  { key: 'reports', label: 'Reports' },
]

/** Sections that grant any loyalty area access (view, edit, or adjust). */
export const LOYALTY_VIEW_SECTIONS: UserSection[] = [
  'cs_loyalty_r',
  'cs_loyalty_w',
  'cs_loyalty_csr',
]

/**
 * Sections that grant loyalty customer/tag edit access.
 * `cs_loyalty_csr` is the help-desk role: it can edit customer records
 * and tags but cannot adjust rank or reward points.
 */
export const LOYALTY_EDIT_SECTIONS: UserSection[] = ['cs_loyalty_w', 'cs_loyalty_csr']

/**
 * Sections that grant loyalty points adjustment access.
 * Restricted to `cs_loyalty_w`; CSRs explicitly cannot adjust points.
 */
export const LOYALTY_ADJUST_SECTIONS: UserSection[] = ['cs_loyalty_w']

/** Sections that grant read access to one-time-use codes. */
export const OTU_VIEW_SECTIONS: UserSection[] = ['cs_otu_r']

export const PORTAL_SECTIONS: SectionOption[] = [
  {
    id: 'cs_locations_r',
    name: 'Locations (Read)',
    description: 'View DBAs, districts, locations, types, price levels, additional taxes',
    group: 'locations',
  },
  {
    id: 'cs_locations_w',
    name: 'Locations (Write)',
    description: 'Create, update, and delete locations and related records',
    group: 'locations',
  },
  {
    id: 'cs_products_r',
    name: 'Products (Read)',
    description: 'View product catalog, categories, units, and types',
    group: 'products',
  },
  {
    id: 'cs_products_w',
    name: 'Products (Write)',
    description: 'Manage product catalog, categories, units, and types',
    group: 'products',
  },
  {
    id: 'cs_inventory_r',
    name: 'Inventory (Read)',
    description: 'View inventory levels, transfers, and audits',
    group: 'inventory',
  },
  {
    id: 'cs_inventory_w',
    name: 'Inventory (Write)',
    description: 'Edit inventory, create transfers, zero-out, receive transfers',
    group: 'inventory',
  },
  {
    id: 'cs_discounts_r',
    name: 'Discounts (Read)',
    description: 'View all discounts',
    group: 'discounts',
  },
  {
    id: 'cs_discounts_w',
    name: 'Discounts (Write)',
    description: 'Create and update discounts',
    group: 'discounts',
  },
  {
    id: 'cs_otu_r',
    name: 'OTU Codes (Read)',
    description: 'Look up one-time-use codes',
    group: 'otu',
  },
  {
    id: 'cs_otu_w',
    name: 'OTU Codes (Write)',
    description: 'Create one-time-use codes',
    group: 'otu',
  },
  {
    id: 'cs_loyalty_r',
    name: 'Loyalty (Read)',
    description: 'View loyalty customers, points, ledger, and tags',
    group: 'loyalty',
  },
  {
    id: 'cs_loyalty_w',
    name: 'Loyalty (Write)',
    description: 'Manage loyalty customers, tags, and adjust points',
    group: 'loyalty',
  },
  {
    id: 'cs_loyalty_csr',
    name: 'Loyalty (CSR)',
    description:
      'Help-desk role: edit customers and tags only (no rank or reward point adjustments)',
    group: 'loyalty',
  },
  {
    id: 'cs_messages_r',
    name: 'Messages (Read)',
    description: 'View messages, delivery status, receipts, and analytics',
    group: 'messages',
  },
  {
    id: 'cs_messages_w',
    name: 'Messages (Write)',
    description: 'Create messages, recall, and update bulk status',
    group: 'messages',
  },
  {
    id: 'cs_promotions_r',
    name: 'Promotions (Read)',
    description: 'View all promotional campaigns',
    group: 'promotions',
  },
  {
    id: 'cs_promotions_w',
    name: 'Promotions (Write)',
    description: 'Create and update promotional campaigns',
    group: 'promotions',
  },
  {
    id: 'cs_pos_users_r',
    name: 'POS Users (Read)',
    description: 'View POS terminal users, types, and attributes',
    group: 'pos_users',
  },
  {
    id: 'cs_pos_users_w',
    name: 'POS Users (Write)',
    description: 'Create and update POS terminal users',
    group: 'pos_users',
  },
  {
    id: 'cs_oos_ledger_r',
    name: 'OOS Ledger (Read)',
    description: 'Query the out-of-scope (card payment) transaction events ledger',
    group: 'oos_ledger',
  },
  {
    id: 'cs_reports_sales_r',
    name: 'Sales Reports',
    description: 'Payment totals, sales summaries, and item sales reports',
    group: 'reports',
  },
  {
    id: 'cs_reports_inventory_r',
    name: 'Inventory Reports',
    description: 'Inventory transfers, zero-day, incoming/received reports',
    group: 'reports',
  },
  {
    id: 'cs_reports_jobs_r',
    name: 'Report Jobs (view)',
    description: 'See the report jobs list and download finished reports',
    group: 'reports',
  },
  {
    id: 'cs_reports_jobs_w',
    name: 'Report Jobs (run)',
    description: 'Run report jobs on the data platform and cancel running ones',
    group: 'reports',
  },
]

/** Fast lookup of known portal (cs_*) section ids. */
const PORTAL_SECTION_IDS: ReadonlySet<string> = new Set(PORTAL_SECTIONS.map((s) => s.id))

/**
 * A deprecated section permission (a pre-cs_* value such as `reports_all` or an
 * `*_vo` view-only role) that may still linger on a user from the legacy
 * admin/reports/op services.
 */
interface LegacySectionOption {
  id: string
  name?: string
  description?: string
}

/**
 * Enrichment catalog for legacy section values, keyed by raw permission id:
 * friendly name/description shown by the Legacy Sections Access card. Seeded
 * from the documented legacy Section-Based Access list (see CLAUDE.md); these
 * are the pre-cs_* values that may still linger on users from the legacy
 * admin/reports/op services.
 *
 * Any legacy value NOT listed here (e.g. an ad-hoc `*_vo` view-only role) still
 * renders by its raw key, so a user can always be stripped of it regardless of
 * this catalog.
 */
const LEGACY_SECTION_CATALOG: Partial<Record<string, { name: string; description?: string }>> = {
  admin: {
    name: 'Administrator',
    description: 'Legacy full administrative access to all sections',
  },
  locations: { name: 'Locations', description: 'Legacy access to DBAs, districts, and locations' },
  products_all: { name: 'Products', description: 'Legacy full product catalog access' },
  inventory: { name: 'Inventory', description: 'Legacy inventory management access' },
  loyalty_csr: { name: 'Loyalty (CSR)', description: 'Legacy help-desk loyalty access' },
  loyalty_all: { name: 'Loyalty', description: 'Legacy full loyalty access' },
  pos_kit: { name: 'POS Kit', description: 'Legacy POS kit configuration access' },
  pos_users: { name: 'POS Users', description: 'Legacy POS terminal user management access' },
  configuration: { name: 'Configuration', description: 'Legacy system configuration access' },
  reports: { name: 'Reports', description: 'Legacy reports access' },
  reports_sales: { name: 'Sales Reports', description: 'Legacy sales reports access' },
  reports_inventory: { name: 'Inventory Reports', description: 'Legacy inventory reports access' },
  vip_signups: { name: 'VIP Signups', description: 'Legacy VIP signup management access' },
  schedule_reports: { name: 'Scheduled Reports', description: 'Legacy scheduled reports access' },
  jobs: { name: 'Report Jobs', description: 'Legacy report jobs access' },
}

/** A section is legacy when it is not one of the known cs_* portal sections. */
function isLegacySection(id: string): boolean {
  return id.length > 0 && !PORTAL_SECTION_IDS.has(id)
}

/**
 * Extracts the deprecated (non-cs_*) section ids from a user's section list,
 * preserving order and dropping duplicates.
 */
export function getLegacySectionIds(sections: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const id of sections) {
    if (isLegacySection(id) && !seen.has(id)) {
      seen.add(id)
      result.push(id)
    }
  }
  return result
}

/** Enriched label/description for a legacy id, falling back to the raw key. */
export function getLegacySectionOption(id: string): LegacySectionOption {
  // eslint-disable-next-line security/detect-object-injection -- id is a section permission key, not a user-controlled path
  const enrichment = LEGACY_SECTION_CATALOG[id]
  return enrichment ? { id, ...enrichment } : { id }
}
